<script setup lang="ts">
import { Popover } from 'primevue';
import type { MenuItemCommandEvent } from 'primevue/menuitem';
import type { DropdownMenuItem } from '../types/common';

const { t } = useI18n();

withDefaults(defineProps<{
    menuList: DropdownMenuItem[],
    dark?: boolean,
    icon?: string
    disabled?: boolean
    buttonClass?: string
}>(), {
    dark: false,
    icon: 'pi pi-ellipsis-v',
    buttonClass: ''
});

const emit = defineEmits(['menuShown', 'menuHidden']);

const menu = ref<InstanceType<typeof Popover> | null>(null);
const isOpen = ref(false);

const {
    unregisterDropdownMenu,
    setActiveDropdownMenu,
    closeActiveDropdownMenu,
    getActiveDropdownMenu,
    currentOpenDropdown
} = useGlobalDropdownMenu();

// Unique ID for this dropdown instance
const dropdownId = `dropdown-${generateId(false)}`;

const hideDropdown = (): void => {
    menu?.value?.hide();
};

// Watch for changes in global dropdown state
watch(currentOpenDropdown, (newActiveId) => {
    // If another dropdown becomes active and it's not this one, close this dropdown
    if (newActiveId && newActiveId !== dropdownId) {
        hideDropdown();
    }
});

// Cleanup on unmount
onUnmounted(() => {
    unregisterDropdownMenu(dropdownId);
});

const onShow = (): void => {
    isOpen.value = true;
    setActiveDropdownMenu(dropdownId);
    emit('menuShown');
};

const onHide = (): void => {
    isOpen.value = false;
    if (getActiveDropdownMenu() === dropdownId) {
        closeActiveDropdownMenu();
    }
    emit('menuHidden');
};
const showHideMenu = (event: Event): void => {
    menu.value?.toggle(event);
    event.stopPropagation();
    event.preventDefault();
};

const handleMenuClick = (menuItem: DropdownMenuItem, event: Event) => {
    if (menuItem.disabled) {
        return;
    }

    hideDropdown();
    closeActiveDropdownMenu();

    if (menuItem.command) {
        menuItem.command(event as unknown as MenuItemCommandEvent);
    }
};
</script>

<template>
    <span class="inline-flex items-center">
        <span
            v-if="!$slots.default"
            :class="[`${icon}`, buttonClass, dark ? 'text-white cursor-pointer' : 'cursor-pointer']"
            role="button"
            tabindex="0"
            aria-haspopup="menu"
            :aria-expanded="isOpen"
            @click="showHideMenu"
            @keydown.enter="showHideMenu"
        />
        <span
            v-else
            role="button"
            tabindex="0"
            aria-haspopup="menu"
            :aria-expanded="isOpen"
            @click="showHideMenu"
            @keydown.enter="showHideMenu"
        >
            <slot />
        </span>

        <VLPopover
            ref="menu"
            class="mt-2"
            unstyled
            @show="onShow"
            @hide="onHide"
        >
            <div
                class="flex flex-col overlay-panel rounded-[8px] bg-white"
                :class="dark ? 'dark' : ''"
                role="menu"
            >
                <div
                    v-for="(menuItem, index) in menuList"
                    :key="index"
                    v-tooltip="menuItem.disabled ? menuItem.tooltip : undefined"
                    role="menuitem"
                    :class="menuItem.disabled ? 'py-2 px-4 disabled' : 'group py-2 px-4 cursor-pointer'"
                    @click="handleMenuClick(menuItem, $event)"
                >
                    <div class="flex items-start gap-2">
                        <span
                            v-if="menuItem.icon"
                            v-tooltip="!menuItem.disabled ? menuItem.tooltip : undefined"
                            :class="[`${menuItem.icon} mt-0.5 text-sm`, dark ? 'text-white group-hover:text-blue-600' : 'group-hover:text-blue-600']"
                        />

                        <img
                            v-if="menuItem.iconImage"
                            :src="menuItem.iconImage"
                            class="h-[16px] mt-0.5"
                            alt="icon"
                        >

                        <div class="flex flex-col">
                            <span
                                v-tooltip="!menuItem.disabled ? menuItem.tooltip : undefined"
                                :class="dark ? 'text-white group-hover:text-blue-600' : 'group-hover:text-blue-600'"
                            >
                                {{ menuItem.label ? menuItem.label : t(menuItem.labelKey || '') }}
                            </span>
                            <span
                                v-if="menuItem.subheader"
                                class="text-xs text-muted leading-tight"
                            >
                                {{ menuItem.subheader }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </VLPopover>
    </span>
</template>

<style scoped>
.overlay-panel{
box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
border: 1px solid var(--main-border-color);
}
.dark {
background-color: var(--primary-text-color);
color: var(--white-color);
}

.group:hover .group-hover\:text-blue-600 {
    color: var(--secondary-color-hover);
}

.group:hover {
background-color: var(--action-btn-hover);
}
.disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.disabled > * {
    pointer-events: none;
}
</style>
